const configuredOrigin = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "");
export const API_ORIGIN = configuredOrigin ?? "";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
    public requestId?: string,
  ) {
    super(requestId ? `${message}\nКод запроса: ${requestId}` : message);
    this.name = "ApiError";
  }
}

const ERROR_MESSAGES_RU: Record<string, string> = {
  UPLOAD_CAPACITY_BUSY: "Сервер занят сохранением данных. Локальный снимок сохранён; повторите синхронизацию позже.",
  UPLOAD_STAGING_LIMIT: "Файл превышает технический предел загрузки. Обратитесь к администрации; локальный снимок сохранён.",
  SUBSCRIPTION_REQUIRED: "Для нового запуска нужно продлить подписку. Сохранение работающих профилей доступно.",
  RUN_LIMIT_REACHED: "Достигнут общий лимит одновременных запусков команды.",
  MEMBER_RUN_LIMIT_REACHED: "Достигнут ваш лимит одновременных запусков.",
  PROFILE_LIMIT_REACHED: "Достигнут лимит профилей, включая архив и корзину.",
  SEAT_LIMIT_REACHED: "В команде нет свободных мест. Приглашения тоже занимают место.",
  DEVICE_LIMIT_REACHED: "Достигнут лимит компьютеров.",
  WORKSPACE_ACCESS_DENIED: "У вашей учётной записи нет доступа к этой команде.",
  SESSION_ORIGIN_INVALID: "Войдите через единый вход GShard для этого сайта.",
  FEATURE_UNAVAILABLE: "Функция недоступна в тарифе команды.",
  EXPORT_DISABLED: "Экспорт профилей недоступен.",
  AUTH_REQUIRED: "Требуется войти в систему.",
  TOKEN_INVALID: "Сеанс истёк. Войдите снова.",
  SESSION_REVOKED: "Сеанс завершён. Войдите снова.",
  WEB_DEVICE_MISMATCH: "Идентификатор браузера изменился. Войдите снова.",
  DEVICE_REVOKED: "Доступ этого браузера отозван администратором.",
  ACCOUNT_BLOCKED: "Учётная запись заблокирована.",
  ACCOUNT_DISABLED: "Учётная запись отключена.",
  ACCOUNT_CHANGED: "Учётная запись изменена другим администратором. Закройте окно, обновите список и откройте её заново.",
  ADMIN_REQUIRED: "Требуются права администратора.",
  SUPERADMIN_REQUIRED: "Требуются права суперадминистратора.",
  CSRF_REQUIRED: "Не найден защитный токен запроса. Обновите страницу.",
  CSRF_INVALID: "Защитный токен запроса устарел. Обновите страницу.",
  INTERNAL_ERROR: "Сервер не смог выполнить запрос.",
  POSTGRES_UNAVAILABLE: "База данных временно недоступна.",
  REDIS_UNAVAILABLE: "Служба координации временно недоступна.",
  OBJECT_STORAGE_UNAVAILABLE: "Хранилище профилей временно недоступно.",
  PROFILE_NOT_FOUND: "Профиль не найден.",
  PROFILE_TEMPLATE_NOT_FOUND: "Шаблон конфигурации не найден.",
  PROFILE_TEMPLATE_EXISTS: "Шаблон с таким названием уже существует.",
  PROFILE_TEMPLATE_INVALID: "Проверьте название и описание шаблона.",
  PLATFORM_CONSTRAINTS_INVALID: "Неверный JSON ограничений платформы.",
  FINGERPRINT_CONFIG_INVALID: "Неверный JSON конфигурации отпечатка.",
  PROFILE_PLATFORM_UNSUPPORTED: "Этот профиль нельзя запустить на текущей платформе.",
  PROFILE_METADATA_INVALID: "Метаданные профиля превышают допустимые ограничения.",
  PROFILE_NAME_INVALID: "Укажите корректное название профиля.",
  PROFILE_LOCKED: "Профиль сейчас используется. Завершите активную аренду и повторите действие.",
  PROFILE_ACCESS_DENIED: "У вас нет доступа к этому профилю.",
  PROFILE_DISABLED: "Профиль отключён администратором.",
  LEASE_CONFLICT: "Профиль уже запущен другим пользователем или устройством.",
  LEASE_EXPIRED: "Срок аренды профиля истёк. Обновите данные и повторите запуск.",
  PROXY_UNAVAILABLE: "Назначенный прокси недоступен. Проверьте его состояние и повторите попытку.",
  PROXY_NOT_FOUND: "Назначенный прокси не найден.",
  PROXY_INVALID: "Проверьте адрес, порт и учётные данные прокси.",
  RECOVERY_REQUIRED: "Сначала завершите ожидающее восстановление профиля.",
  RECOVERY_NOT_ACTIVE: "Для этого профиля нет активного восстановления. Обновите страницу.",
  REVISION_CONFLICT: "Профиль был изменён параллельно. Обновите данные и повторите действие.",
  CLIENT_UPDATE_REQUIRED: "Для продолжения обновите приложение GShard.",
  RELEASE_ITEMS_INVALID: "Добавьте в релиз от одной до 10 000 ревизий, не более одной на профиль.",
  RELEASE_REVISION_INVALID: "Выбранная ревизия недоступна для публикации.",
  RELEASE_NAME_INVALID: "Укажите корректное уникальное название релиза.",
  RELEASE_NAME_EXISTS: "Релиз с таким названием уже существует.",
  RELEASE_EMPTY: "Добавьте в релиз хотя бы одну ревизию профиля.",
  RATE_LIMITED: "Слишком много запросов. Повторите попытку позже.",
  REFRESH_TOKEN_REUSED: "Этот сеанс завершён. Войдите снова.",
};

function cookie(name: string) {
  return document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

async function parse(response: Response) {
  const text = await response.text();
  let body: any = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      if (response.ok) throw new Error("Сервер вернул ответ в неизвестном формате.");
    }
  }
  if (!response.ok) {
    const error = body?.error ?? body ?? {};
    const code = error.code ?? "HTTP_ERROR";
    const fallback = response.status === 404 ? "Ресурс не найден." : response.status === 405 ? "Эта операция требует обновления серверной части GShard." : response.statusText || `Ошибка HTTP ${response.status}`;
    const requestId = response.headers.get("x-request-id") ?? undefined;
    throw new ApiError(response.status, code, ERROR_MESSAGES_RU[code] ?? error.message ?? fallback, error.details, requestId);
  }
  return body;
}

let refreshing: Promise<void> | null = null;
const refreshLock = `gshard-session:${API_ORIGIN || "same-origin"}`;

async function refresh() {
  if (!refreshing) {
    const rotate = async () => {
      // A different tab may have refreshed the shared cookies while we waited.
      const current = await fetch(`${API_ORIGIN}/api/v1/auth/me`, { method: "GET", credentials: "include", cache: "no-store" });
      if (current.ok) return;
      if (current.status !== 401) { await parse(current); return; }
      await parse(await fetch(`${API_ORIGIN}/api/v1/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", "x-csrf-token": decodeURIComponent(cookie("msx_csrf") ?? "") },
        body: "{}",
      }));
    };
    // The refresh token is single-use. An in-memory promise alone cannot
    // serialize two tabs and must not weaken the server's replay protection.
    refreshing = (typeof navigator !== "undefined" && navigator.locks
      ? navigator.locks.request(refreshLock, rotate)
      : Promise.reject(new Error("Для восстановления сеанса откройте панель в актуальном браузере по HTTPS и войдите снова.")))
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

export async function api<T = any>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  const support = sessionStorage.getItem("gshard-support");
  if (support && /^[0-9a-f-]{36}$/.test(support)) headers.set("x-gshard-support", support);
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = cookie("msx_csrf");
    if (csrf) headers.set("x-csrf-token", decodeURIComponent(csrf));
  }
  const response = await fetch(`${API_ORIGIN}${path}`, { ...init, method, headers, credentials: "include" });
  const sessionEndpoint = /^\/api\/v1\/auth\/(me|logout)$/.test(path.split("?")[0]);
  if (response.status === 401 && retry && (sessionEndpoint || !path.startsWith("/api/v1/auth/"))) {
    await refresh();
    return api<T>(path, init, false);
  }
  return parse(response);
}

export const post = <T = any>(path: string, body: unknown) => api<T>(path, { method: "POST", body: JSON.stringify(body) });
export const patch = <T = any>(path: string, body: unknown) => api<T>(path, { method: "PATCH", body: JSON.stringify(body) });
export const put = <T = any>(path: string, body: unknown) => api<T>(path, { method: "PUT", body: JSON.stringify(body) });
